/**
 * 用户认证事件管理器
 * 用于监听和触发登录态变更事件
 *
 * @example
 * // 监听用户信息变更事件
 * import { authEventManager } from '@/utils/authEventManager';
 *
 * // 在组件中订阅事件
 * useEffect(() => {
 *   const unsubscribe = authEventManager.on('userInfoChange', (data) => {
 *     console.log('用户信息变更:', data);
 *     if (data.isLogin) {
 *       console.log('用户已登录:', data.uid, data.nick);
 *     } else {
 *       console.log('用户已退出登录');
 *     }
 *   });
 *
 *   // 清理订阅
 *   return () => {
 *     unsubscribe();
 *   };
 * }, []);
 *
 * @example
 * // 在非 React 环境中使用
 * authEventManager.on('userInfoChange', (data) => {
 *   // 处理登录态变更
 *   if (data.isLogin) {
 *     // 用户登录成功，刷新页面数据
 *     refreshUserData();
 *   } else {
 *     // 用户退出登录，清理缓存
 *     clearUserCache();
 *   }
 * });
 */

/**
 * 认证事件映射
 */
export interface AuthEventMap {
  /** 用户信息变更事件（登录、登出、信息更新统一触发此事件） */
  userInfoChange: (data: AuthEventData) => void;
}

/**
 * 认证事件数据
 */
export interface AuthEventData {
  /** 用户ID */
  uid?: string;
  /** 用户昵称 */
  nick?: string;
  /** 用户头像 */
  avatar?: string;
  /** 登录状态 */
  isLogin: boolean;
  /** 时间戳 */
  timestamp: number;
}

type EventHandler<T extends keyof AuthEventMap> = AuthEventMap[T];

/**
 * 用户认证事件管理器
 */
export class AuthEventManager {
  private static instance: AuthEventManager;

  /**
   * 获取单例实例
   */
  public static getInstance(): AuthEventManager {
    if (!AuthEventManager.instance) {
      AuthEventManager.instance = new AuthEventManager();
    }
    return AuthEventManager.instance;
  }

  private eventHandlers: Map<string, Set<Function>> = new Map();

  private constructor() {
    // 私有构造函数，确保单例
  }

  /**
   * 订阅事件
   */
  public on<T extends keyof AuthEventMap>(event: T, handler: EventHandler<T>): () => void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }

    this.eventHandlers.get(event)!.add(handler);
    console.log(`[AuthEventManager] 订阅事件: ${event}`);

    // 返回取消订阅函数
    return () => this.off(event, handler);
  }

  /**
   * 取消订阅事件
   */
  public off<T extends keyof AuthEventMap>(event: T, handler: EventHandler<T>): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler);
      console.log(`[AuthEventManager] 取消订阅事件: ${event}`);

      // 如果没有监听器了，清理 Map
      if (handlers.size === 0) {
        this.eventHandlers.delete(event);
      }
    }
  }

  /**
   * 触发事件
   */
  public emit<T extends keyof AuthEventMap>(event: T, ...args: Parameters<EventHandler<T>>): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers && handlers.size > 0) {
      console.log(`[AuthEventManager] 触发事件: ${event}`, args[0]);
      handlers.forEach((handler) => {
        try {
          (handler as Function)(...args);
        } catch (error) {
          console.error(`[AuthEventManager] 事件处理器执行失败 (${event}):`, error);
        }
      });
    } else {
      console.log(`[AuthEventManager] 触发事件: ${event}，但没有监听器`);
    }
  }

  /**
   * 一次性订阅（触发一次后自动取消订阅）
   */
  public once<T extends keyof AuthEventMap>(event: T, handler: EventHandler<T>): void {
    // 创建包装处理器，直接使用 EventHandler 签名而不是 rest 参数
    const wrappedHandler: EventHandler<T> = (data: AuthEventData) => {
      handler(data);
      this.off(event, wrappedHandler);
    };

    this.on(event, wrappedHandler);
  }

  /**
   * 移除所有监听器
   */
  public removeAllListeners(): void {
    console.log('[AuthEventManager] 清除所有事件监听器');
    this.eventHandlers.clear();
  }

  /**
   * 获取某个事件的监听器数量
   */
  public listenerCount(event: keyof AuthEventMap): number {
    return this.eventHandlers.get(event)?.size || 0;
  }
}

/**
 * 导出单例实例
 */
export const authEventManager = AuthEventManager.getInstance();

/**
 * 便捷方法：触发用户信息变更事件（登录）
 */
export function emitLoginEvent(data: Omit<AuthEventData, 'timestamp'>): void {
  authEventManager.emit('userInfoChange', {
    ...data,
    isLogin: true,
    timestamp: Date.now(),
  });
}

/**
 * 便捷方法：触发用户信息变更事件（退出登录）
 */
export function emitLogoutEvent(): void {
  authEventManager.emit('userInfoChange', {
    uid: '',
    nick: '',
    avatar: '',
    isLogin: false,
    timestamp: Date.now(),
  });
}

/**
 * 便捷方法：触发用户信息变更事件（信息更新）
 */
export function emitUserInfoUpdateEvent(data: Omit<AuthEventData, 'timestamp'>): void {
  authEventManager.emit('userInfoChange', {
    ...data,
    timestamp: Date.now(),
  });
}
